import type { VariantType } from 'notistack'

import { icons } from 'src/theme/tokens.generated'

// ----------------------------------------------------------------------
// Severity picks the tone. Blue means no action needed.
//
// The console shipped four tones for five meanings, so the fifth borrowed one.
// *Your account does not have a subscription* (the sentence that explains why
// half the page below it is inert) wore the same blue as *This provider has
// no subscription, so there are no terms to set*, which genuinely is a
// footnote. Reading the first one as ignorable costs an operator the whole
// route.
//
// The distinction is about what is expected of the reader, not about how bad
// the news is:
//
//   info       A fact. Nothing is blocked, nothing is expected of you.
//   tip        A heads-up or a way forward. Nothing is wrong and nothing is
//              owed; it says how something works, what you may do, or that
//              you may stop and come back.
//   attention  Something is not set up, and a capability is off until it is.
//              You can fix it, and the alert says how.
//   warning    Something will go wrong soon if it is left alone.
//   error      It already failed.
//   success    It worked.
//
// `attention` is the one that was missing, and it is the tone for every "you
// have not finished setting this up" state in the console: no subscription,
// Stripe not connected, Tesla not connected, an unfinished setup item. It is
// also the only tone that carries an action, because that is what separates it
// from `info`: if there is something to do, it is not a footnote.
//
// The union is here rather than in the theme so a screen names a tone and a
// copy key can own it, which is what stops one sentence shipping in two tones
// on two routes.
// ----------------------------------------------------------------------

/**
 * Every tone an alert can take, in the order of what is expected of the reader:
 * nothing, then something, then something soon, then it is too late.
 *
 * `tip` sits beside `info` because it asks for no more than a fact does. It is
 * the one a panel reaches for when a line needs to stand out without being
 * news: the onboarding panel's "nothing here is final" was a paragraph a size
 * smaller than its neighbours, which is emphasis by shrinking, and so were
 * App config's note on the per-fleet switches and Report exports' note that
 * no file is kept (made tips on 2026-11-01, in the pale yellow, asked for as
 * "yellowish tip cards").
 *
 * `success` sits at the end because it is the one that reports rather than
 * asks. Ordered deliberately: the theme builds its per-tone slots by iterating
 * this, so a new tone added here gets its styles without a second edit.
 */
export const ALERT_TONES = [
  'info',
  'tip',
  'attention',
  'warning',
  'error',
  'success',
] as const

export type AlertTone = (typeof ALERT_TONES)[number]

/**
 * The tone a toast takes, per notistack variant.
 *
 * A toast and a hint say the same five kinds of thing, so they may not look
 * like two systems. The provider renders the themed `Alert` and this record is
 * the one place the two vocabularies meet. Before it, the console carried a
 * second tone treatment written against notistack v2's class names, which
 * notistack 3 does not emit, so every toast wore the library's own hard-coded
 * hues while the theme's five tones sat unused beside them.
 *
 * `default` is notistack's no-variant-given case, and a toast nobody chose a
 * tone for is a fact you may ignore, which is `info`.
 *
 * There is no `attention` here, and there cannot be: `attention` means a
 * capability is off until somebody finishes something, which is not a sentence
 * that may vanish after three seconds. The record is typed against notistack's
 * own `VariantType`, so a library that grows a sixth variant fails to compile
 * here rather than quietly falling back to blue.
 */
export const TOAST_TONE = {
  success: 'success',
  error: 'error',
  warning: 'warning',
  info: 'info',
  default: 'info',
} as const satisfies Record<VariantType, AlertTone>

/**
 * The tone that means "a capability is off until you finish something".
 *
 * Named rather than spelled at each call site, so the sweep that moved every
 * not-set-up state off `info` can be read as one decision, and so the next
 * such state is written by reaching for the name rather than by copying
 * whichever neighbouring alert was nearest.
 */
export const NOT_SET_UP: AlertTone = 'attention'

/**
 * The mark a tone carries.
 *
 * From the token source, so a tone cannot pick up a second mark on another
 * surface, and all five are stroked: the console draws stroked glyphs
 * everywhere else, and a filled disc with a white glyph knocked out of it was
 * the one solid mark in the product.
 */
export const toneGlyph = (tone: AlertTone): string => icons.tone[tone]

/**
 * The colour-role name of the ink that reads on a tone's solid fill.
 *
 * A solid fill needs an ink chosen for it, and the six do not agree on which:
 * the yellows and the orange are light fills and take near-black, the blue is
 * a dark fill and takes white. Guessing this per component is how a filled
 * alert came to put white on the accent at 3.44:1 in dark mode and stay there
 * (no screen uses the filled variant, so nothing reported it).
 *
 * `attention` and `tip` borrow `onWarning` rather than getting a role of
 * their own, because the three fills are the same kind of thing: a bright
 * warm fill that needs dark ink. A separate role would be a second name for
 * one decision.
 */
export const TONE_INK = {
  info: 'onAccent',
  tip: 'onWarning',
  attention: 'onWarning',
  warning: 'onWarning',
  error: 'onError',
  success: 'onSuccess',
} as const satisfies Record<AlertTone, string>

/**
 * Whether a tone may carry an action.
 *
 * Only `attention` asks for something, so only `attention` offers the remedy.
 * An `error` states what already failed and a retry belongs to the control
 * that failed, not to the sentence about it; `info` carrying a button is the
 * contradiction this whole module exists to remove.
 */
export const toneCarriesAction = (tone: AlertTone): boolean =>
  tone === NOT_SET_UP

// ----------------------------------------------------------------------
// The tone as MUI sees it.
//
// MUI ships four severities. It takes a fifth at run time without complaint
// (its built-in per-colour styles come from the palette entries carrying both
// `main` and `light`, so a tone with no palette slot simply matches none of
// them and the theme override supplies all of it) but `severity` is an
// `OverridableStringUnion`, so the fifth has to be declared before a screen
// can name it.
//
// Declared here, beside the union it widens, rather than in a types folder
// nothing reads: the two have to move together or a tone exists in one of them
// and not the other.
// ----------------------------------------------------------------------

declare module '@mui/material/Alert' {
  interface AlertPropsColorOverrides {
    attention: true
    tip: true
  }
}
